import { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Send, Loader2, Shield } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

/**
 * Public comments / Q&A section for an event. Reuses the inquiries collection
 * with `is_public=true`. Anyone can post a comment (no login). Producer
 * replies are visible inline with a distinctive "Productora" badge.
 */
export default function PublicCommentsSection({ event, accentColor = '#D4AF37' }) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const pollRef = useRef(null);

  const fetchComments = useCallback(async () => {
    if (!event?.id) return;
    try {
      const res = await api.get(`/inquiries/public/event/${event.id}`);
      setThreads(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      // Silent fail — section just stays empty
    } finally {
      setLoading(false);
    }
  }, [event?.id]);

  useEffect(() => {
    fetchComments();
    // Poll every 30s while the tab is visible
    pollRef.current = setInterval(() => {
      if (document.visibilityState === 'visible') fetchComments();
    }, 30000);
    return () => clearInterval(pollRef.current);
  }, [fetchComments]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedBody = body.trim();
    if (trimmedName.length < 2) {
      toast.error('Ingresá tu nombre (mínimo 2 caracteres)');
      return;
    }
    if (trimmedBody.length < 2) {
      toast.error('Escribí un comentario');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/inquiries', {
        event_id: event.id,
        buyer_name: trimmedName,
        buyer_email: email.trim() || null,
        message: trimmedBody,
        is_public: true,
      });
      const pending = res?.data?.pending_approval;
      toast.success(res?.data?.message || (pending ? 'Comentario enviado, será revisado por la productora' : 'Comentario publicado'));
      setBody('');
      // Optimistic re-fetch (won't show pending ones until approved)
      fetchComments();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'No se pudo publicar el comentario';
      toast.error(typeof msg === 'string' ? msg : 'Error al publicar');
    } finally {
      setSubmitting(false);
    }
  };

  const formatWhen = (iso) => {
    try {
      const d = new Date(iso);
      const diff = (Date.now() - d.getTime()) / 1000;
      if (diff < 60) return 'hace instantes';
      if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
      if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
      return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
    } catch {
      return '';
    }
  };

  return (
    <div
      className="w-full"
      data-testid="event-public-comments-section"
    >
      <div className="flex items-center gap-3 mb-6">
        <MessageSquare size={14} style={{ color: accentColor }} />
        <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-white/40">
          Preguntas y comentarios
        </h2>
      </div>

        {/* Composer */}
        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-6"
          data-testid="public-comment-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <input
              type="text"
              placeholder="Tu nombre"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              required
              data-testid="public-comment-name-input"
              className="px-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
            />
            <input
              type="email"
              placeholder="Email (opcional, para recibir respuesta)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="public-comment-email-input"
              className="px-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
            />
          </div>
          <textarea
            placeholder="Hacé tu pregunta o dejá un comentario..."
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={2000}
            rows={3}
            required
            data-testid="public-comment-body-input"
            className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors resize-none"
          />
          <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
            <p className="text-[11px] text-white/40">
              {event?.require_comment_approval
                ? 'Los comentarios son moderados: aparecerán cuando la productora los apruebe.'
                : 'Los comentarios son públicos. No compartas datos personales.'}
            </p>
            <button
              type="submit"
              disabled={submitting}
              data-testid="public-comment-submit"
              style={{ background: accentColor, color: '#000' }}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {submitting ? 'Publicando…' : 'Publicar'}
            </button>
          </div>
        </form>

        {/* List */}
        {loading ? (
          <div className="text-center py-8 text-white/40 text-sm">Cargando comentarios…</div>
        ) : threads.length === 0 ? (
          <div
            className="text-center py-8 text-white/40 text-sm border border-dashed border-white/10 rounded-xl"
            data-testid="public-comments-empty"
          >
            Aún no hay comentarios. Sé el primero en preguntar.
          </div>
        ) : (
          <ul className="space-y-4" data-testid="public-comments-list">
            <AnimatePresence initial={false}>
              {threads.map((thread) => {
                const allMsgs = thread.messages || [];
                const first = allMsgs[0];
                const replies = allMsgs.slice(1);
                if (!first) return null;
                return (
                  <motion.li
                    key={thread.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden"
                    data-testid={`public-comment-${thread.id}`}
                  >
                    {/* First message (buyer) */}
                    <div className="px-5 py-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-white">
                          {first.sender_name || thread.buyer_name || 'Anónimo'}
                        </span>
                        <span className="text-[11px] text-white/40">
                          {formatWhen(first.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-white/80 leading-relaxed whitespace-pre-line">
                        {first.body}
                      </p>
                    </div>

                    {/* Replies */}
                    {replies.length > 0 && (
                      <div className="border-t border-white/5 bg-white/[0.015]">
                        {replies.map((m) => {
                          const isProducer = m.sender_type === 'producer';
                          return (
                            <div
                              key={m.id || `${m.created_at}-${m.sender_type}`}
                              className="px-5 py-3 border-b border-white/5 last:border-b-0"
                              data-testid={isProducer ? 'public-comment-producer-reply' : 'public-comment-reply'}
                            >
                              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                <span className="text-xs font-semibold text-white/90">
                                  {m.sender_name || (isProducer ? 'Productora' : 'Usuario')}
                                </span>
                                {isProducer && (
                                  <span
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                                    style={{
                                      background: `${accentColor}25`,
                                      color: accentColor,
                                    }}
                                  >
                                    <Shield size={9} /> Productora
                                  </span>
                                )}
                                <span className="ml-auto text-[11px] text-white/40">
                                  {formatWhen(m.created_at)}
                                </span>
                              </div>
                              <p className="text-sm text-white/75 leading-relaxed whitespace-pre-line">
                                {m.body}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </motion.li>
                );
              })}
            </AnimatePresence>
          </ul>
        )}
    </div>
  );
}
